import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, FileText, Check, X, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmButton } from "@/components/ConfirmButton";
import { SyncStatusCard } from "@/components/contracts/SyncStatusCard";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EmployeePicker = ({ value, onChange, testId }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/employees", { params: { stato: "tutti" } }).then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Scegli dipendente" /></SelectTrigger>
      <SelectContent><SelectItem value="none">— Scegli dipendente —</SelectItem>{list.map((e) => <SelectItem key={e.id} value={e.id}>{e.cognome} {e.nome}{e.codice_fiscale ? ` · ${e.codice_fiscale}` : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const TIPO = { contratto: "Contratto", unilav: "UNILAV", busta_paga: "Busta paga", cu: "CU", altro: "Altro" };

const ReviewItem = ({ r, onChanged }) => {
  const [emp, setEmp] = useState(r.employee_id || "");
  const [busy, setBusy] = useState(false);
  const p = r.proposed || {};
  const act = async (fn) => { setBusy(true); try { await fn(); onChanged(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  return (
    <li className="rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid={`review-item-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{r.filename} <span className="text-xs font-normal text-slate-500">· {TIPO[p.tipo_documento] || p.tipo_documento || "?"}</span></p>
          <p className="text-xs text-amber-900" data-testid={`review-reason-${r.id}`}>{r.reason}</p>
          <p className="mt-1 text-xs text-slate-500">Fonte: {r.origine === "email" ? `email ${r.email_from || ""} ${r.email_date ? "· " + formatDate(r.email_date) : ""} ${r.email_subject ? "· " + r.email_subject : ""}` : "caricamento manuale"}</p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/review/${r.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#3d6e49] underline" data-testid={`review-file-${r.id}`}><FileText className="h-3.5 w-3.5" /> Apri documento</a>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
        {[["Lavoratore", `${p.cognome || ""} ${p.nome || ""}`.trim() || "—"], ["Codice fiscale", p.codice_fiscale || "—"], ["Comunicazione", p.tipo_comunicazione || "—"],
          ["Tipologia", p.tipologia_contratto || "—"], ["Mansione", p.mansione || "—"], ["Ore/sett.", p.ore_settimanali ?? "—"],
          ["Inizio", p.data_inizio ? formatDate(p.data_inizio, false) : "—"], ["Fine", p.data_fine ? formatDate(p.data_fine, false) : "—"]].map(([k, v]) => (
          <div key={k}><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900">{v}</dd></div>))}
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="w-64"><EmployeePicker value={emp} onChange={setEmp} testId={`review-employee-${r.id}`} /></div>
        <Button size="sm" disabled={busy || !emp} onClick={() => act(() => api.post(`/contracts/review/${r.id}/accept`, { employee_id: emp }).then(() => toast.success("Documento importato")))} data-testid={`review-accept-${r.id}`}><Check className="mr-1 h-4 w-4" /> Importa</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.post(`/contracts/review/${r.id}/reject`).then(() => toast.success("Scartato")))} data-testid={`review-reject-${r.id}`}><X className="mr-1 h-4 w-4" /> Scarta</Button>
      </div>
    </li>
  );
};

export default function Imports() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ status: "never" });
  const [review, setReview] = useState([]);
  const [settings, setSettings] = useState(null);
  const [starting, setStarting] = useState(false);
  const [tab, setTab] = useState("pending");

  const load = useCallback(() => {
    api.get("/contracts/sync/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/contracts/review", { params: { status: tab } }).then((r) => setReview(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [tab]);
  useEffect(load, [load]);
  useEffect(() => {
    if (status.status !== "running") return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [status.status, load]);

  const checkNow = async () => {
    setStarting(true);
    try { await api.post("/contracts/sync"); toast.success("Controllo avviato"); setStatus({ status: "running", found: 0 }); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setStarting(false); }
  };
  const resetHistory = async () => {
    try { const { data } = await api.delete("/contracts/processed-emails"); toast.success(`Cronologia azzerata (${data.deleted} email): il prossimo controllo rilegge tutto`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const senders = (settings?.contracts_sender || "").split(/[,;\s]+/).filter(Boolean);
  return (
    <>
      <PageHeader eyebrow="Automazione" title="Importazioni email"
        description="Lettura della casella collegata: il primo controllo esamina tutta la cronologia dei mittenti, i successivi solo le email nuove. Controllo automatico ogni giorno alle 17:00."
        actions={<div className="flex flex-wrap gap-2">
          {user?.role === "admin" && <ConfirmButton testId="reset-email-history-button" variant="outline" title="Rileggere tutta la cronologia?" description="Il prossimo controllo esaminerà di nuovo tutte le email. I documenti già importati non vengono duplicati." onConfirm={resetHistory}><History className="mr-2 h-4 w-4" /> Rileggi tutto</ConfirmButton>}
          <Button onClick={checkNow} disabled={starting || status.status === "running" || !settings?.imap_configured} data-testid="check-now-button">
            {status.status === "running" || starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Controlla ora</Button>
        </div>} />
      {settings && (
        <div className="panel grid grid-cols-1 gap-4 p-5 text-sm sm:grid-cols-4" data-testid="imap-connection-card">
          <div><p className="eyebrow">Casella</p><p className="mt-1 font-medium text-slate-900">{settings.imap_account || "—"}</p>
            <p className={`text-xs ${settings.imap_configured ? "text-emerald-700" : "text-rose-700"}`} data-testid="imap-connection-state">{settings.imap_configured ? "Configurata (IMAP, password per le app)" : "Non collegata"}</p></div>
          <div><p className="eyebrow">Importazione automatica</p><p className="mt-1 font-medium">{settings.auto_sync ? "Abilitata · ogni giorno 17:00" : "Disabilitata"}</p></div>
          <div><p className="eyebrow">Mittenti controllati</p><p className="mt-1 text-xs text-slate-700">{senders.length ? senders.join(", ") : "Tutti"}</p></div>
          <div><p className="eyebrow">Ultimo controllo</p><p className="mt-1 text-xs text-slate-700">{settings.last_check_at ? formatDate(settings.last_check_at) : "Mai"} {settings.historical_done ? "" : "· scansione storica da completare"}</p>
            <Link to="/impostazioni" className="text-xs text-[#3d6e49] underline" data-testid="imports-settings-link">Modifica in Impostazioni</Link></div>
        </div>
      )}
      <SyncStatusCard status={status} unmatched={[]} onChanged={load} />
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-lg font-bold text-slate-900">Da verificare</h3>
          {["pending", "accepted", "rejected"].map((t) => <button key={t} onClick={() => setTab(t)} className={`rounded-full px-3 py-1 text-xs ${tab === t ? "bg-[#3d6e49] text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`review-tab-${t}`}>{{ pending: "In attesa", accepted: "Importati", rejected: "Scartati" }[t]}</button>)}
        </div>
        <ul className="space-y-3" data-testid="review-list">
          {review.map((r) => tab === "pending" ? <ReviewItem key={r.id} r={r} onChanged={load} /> : (
            <li key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`review-item-${r.id}`}>{r.filename} · {r.employee_name || "—"} · {r.resolved_by} · {formatDate(r.resolved_at)}</li>))}
          {review.length === 0 && <li className="panel p-6 text-center text-sm text-slate-500" data-testid="review-empty">Nessun elemento.</li>}
        </ul>
      </div>
    </>
  );
}
